// TelcoTrack — shared entrance motion for dashboard numbers and ECharts.
// Stat tweens are longer than overlay/modal motion (`--tt-duration-overlay` is 180ms).

/** Count-up and chart draw duration. Ease-out, played once (no loop). */
export const STAT_MOTION_MS = 700;

export function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/** ECharts root animation. `cubicOut` is the library ease-out. */
export function echartsEntranceMotion(reduceMotion = prefersReducedMotion()): {
  animation: boolean;
  animationDuration: number;
  animationEasing: 'cubicOut';
  animationDurationUpdate: number;
  animationEasingUpdate: 'cubicOut';
} {
  if (reduceMotion) {
    return {
      animation: false,
      animationDuration: 0,
      animationEasing: 'cubicOut',
      animationDurationUpdate: 0,
      animationEasingUpdate: 'cubicOut',
    };
  }
  return {
    animation: true,
    animationDuration: STAT_MOTION_MS,
    animationEasing: 'cubicOut',
    animationDurationUpdate: STAT_MOTION_MS,
    animationEasingUpdate: 'cubicOut',
  };
}

export type CountUpToken =
  | { type: 'text'; text: string }
  | {
      type: 'number';
      value: number;
      decimals: number;
      decimalSep: ',' | '.';
      groupSep: string | null;
      forcePlus: boolean;
      negSign: '-' | '\u2212';
    };

export interface CountUpModel {
  source: string;
  values: number[];
  tokens: CountUpToken[];
}

/**
 * Czech groups (`1 234,5`), en groups (`1,234.56`), and plain decimals.
 * The last separator wins when both comma and dot are present.
 */
const COUNT_UP_NUMBER_RE =
  /[+\u2212-]?(?:\d{1,3}(?:[ \u00A0\u202F]\d{3})+(?:,\d+)?|\d{1,3}(?:,\d{3})+(?:\.\d+)?|\d{1,3}(?:\.\d{3}){2,}|\d+(?:[.,]\d+)?)/g;

/** `null` means “nothing to show yet”. The words null / NaN never reach the DOM. */
export function sanitizeCountUpSource(value: string | number | null | undefined): string | null {
  if (value == null || value === '') return null;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return '—';
    if (Object.is(value, -0)) return '0';
    if (Number.isInteger(value)) return String(value);
    const trimmed = value.toFixed(4).replace(/\.?0+$/, '');
    return trimmed === '-0' ? '0' : trimmed;
  }
  const trimmed = value.trim();
  if (/^(null|undefined|nan)$/i.test(trimmed)) return '—';
  return value;
}

export function parseCountUp(source: string): CountUpModel {
  const tokens: CountUpToken[] = [];
  const values: number[] = [];
  const re = new RegExp(COUNT_UP_NUMBER_RE.source, 'g');
  let cursor = 0;
  for (const match of source.matchAll(re)) {
    const raw = match[0];
    const index = match.index ?? 0;
    if (index > cursor) {
      tokens.push({ type: 'text', text: source.slice(cursor, index) });
    }
    const token = parseNumberToken(raw);
    if (!token) {
      tokens.push({ type: 'text', text: raw });
    } else {
      tokens.push(token);
      values.push(token.value);
    }
    cursor = index + raw.length;
  }
  if (cursor < source.length) {
    tokens.push({ type: 'text', text: source.slice(cursor) });
  }
  return { source, values, tokens };
}

export function renderCountUp(model: CountUpModel, values: readonly number[]): string {
  let numberIndex = 0;
  let out = '';
  for (const token of model.tokens) {
    if (token.type === 'text') {
      out += token.text;
      continue;
    }
    const value = values[numberIndex] ?? token.value;
    numberIndex += 1;
    out += formatCountUpToken(token, value);
  }
  return out;
}

export function easeOutCubic(t: number): number {
  const clamped = Math.min(1, Math.max(0, t));
  return 1 - Math.pow(1 - clamped, 3);
}

function parseNumberToken(raw: string): Extract<CountUpToken, { type: 'number' }> | null {
  let body = raw;
  let forcePlus = false;
  let negSign: '-' | '\u2212' = '-';
  let negative = false;
  if (body.startsWith('+')) {
    forcePlus = true;
    body = body.slice(1);
  } else if (body.startsWith('\u2212')) {
    negative = true;
    negSign = '\u2212';
    body = body.slice(1);
  } else if (body.startsWith('-')) {
    negative = true;
    negSign = '-';
    body = body.slice(1);
  }
  if (!body) return null;

  const lastComma = body.lastIndexOf(',');
  const lastDot = body.lastIndexOf('.');
  let decimalSep: ',' | '.' | null = null;
  let groupSep: string | null = null;

  if (lastComma >= 0 && lastDot >= 0) {
    decimalSep = lastComma > lastDot ? ',' : '.';
    groupSep = decimalSep === ',' ? groupSeparator(body, '.') : ',';
  } else if (lastComma >= 0) {
    if (/^\d{1,3}(,\d{3})+$/.test(body)) {
      groupSep = ',';
    } else {
      decimalSep = ',';
    }
  } else if (lastDot >= 0) {
    if (/^\d{1,3}(\.\d{3}){2,}$/.test(body)) {
      groupSep = '.';
    } else {
      decimalSep = '.';
    }
  }

  const spaceSep = body.match(/[ \u00A0\u202F]/);
  if (spaceSep && decimalSep !== '.') {
    groupSep = spaceSep[0];
  }

  const parsed = parseNumericBody(body, decimalSep);
  if (!Number.isFinite(parsed.value)) return null;
  const value = negative ? -Math.abs(parsed.value) : parsed.value;
  return {
    type: 'number',
    value,
    decimals: parsed.decimals,
    decimalSep: decimalSep ?? ',',
    groupSep,
    forcePlus,
    negSign,
  };
}

function groupSeparator(body: string, fallback: string): string {
  if (body.includes('\u00A0')) return '\u00A0';
  if (body.includes('\u202F')) return '\u202F';
  if (body.includes(' ')) return ' ';
  return fallback;
}

function parseNumericBody(
  body: string,
  decimalSep: ',' | '.' | null,
): { value: number; decimals: number } {
  if (!decimalSep) {
    const digits = body.replace(/[ \u00A0\u202F.,]/g, '');
    return { value: Number(digits), decimals: 0 };
  }
  const idx = body.lastIndexOf(decimalSep);
  const decimals = Math.max(0, body.length - idx - 1);
  const intPart = body.slice(0, idx).replace(/[ \u00A0\u202F.,]/g, '') || '0';
  const frac = body.slice(idx + 1).replace(/\D/g, '');
  return { value: Number(`${intPart}.${frac}`), decimals };
}

function formatCountUpToken(token: Extract<CountUpToken, { type: 'number' }>, value: number): string {
  if (!Number.isFinite(value)) return '0';
  const negative = value < 0 && Math.abs(value) > 0;
  const abs = Math.abs(value);
  const fixed = abs.toFixed(token.decimals);
  const [intRaw, frac] = fixed.split('.');
  const intPart = token.groupSep
    ? intRaw.replace(/\B(?=(\d{3})+(?!\d))/g, token.groupSep)
    : intRaw;
  let out = token.decimals > 0 ? `${intPart}${token.decimalSep}${frac}` : intPart;
  if (negative) out = `${token.negSign}${out}`;
  else if (token.forcePlus && value > 0) out = `+${out}`;
  return out;
}
